import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import IdentityVaultTab from "../IdentityVaultTab";
import PasswordManagerHeader from "./PasswordManagerHeader";
import AddPassword from "../../Dialogs/AddPassword";
import PasswordList from "./PasswordList";
import * as PasswordManagerAction from "../../../store/passwordManagerService/actions";
import * as ConfigurationActions from "../../../store/configurationService/actions";
import { ApplicationState } from "../../../store";
import ViewPassword from "../../Dialogs/ViewPassword";
import EditPassword from "../../Dialogs/EditPassword";
import ConfirmDeletePassword from "../../Dialogs/ConfirmDeletePassword";
import { DialogComponent } from "./../../common/DialogComponent";
import { useHistory } from "react-router-dom";
import LogoContainer from "../../common/LogoContainer";
import RenderIcons from "../../../Assets/css/_icons/partner_icon/RenderIcons";
import { useTranslation } from "react-i18next";

const PasswordManager = () => {
    const history = useHistory();
    const [addPasswordDialogIsVisible, setAddPasswordDialogIsVisible] = useState(false);
    const [viewPasswordDialogIsVisible, setViewPasswordDialogIsVisible] = useState(false);
    const [editPasswordDialogIsVisible, setEditPasswordDialogIsVisible] = useState(false);
    const [deletePasswordDialogIsVisible, setDeletePasswordDialogIsVisible] = useState(false);
    const [state, setState] = useState({ search: "" });
    const dispatch = useDispatch();
    const passwordListLoading = useSelector((state: ApplicationState) => (state.passwordManagerState ? state.passwordManagerState.loadingPasswordList : false));
    const requestRequestPasswordList = () => dispatch(PasswordManagerAction.actionCreators.requestRequestPasswordList());
    const passwordList = useSelector((state: ApplicationState) => (state.passwordManagerState ? state.passwordManagerState.passwordList : []));
    const isPasswordsAvailable = useSelector((state: ApplicationState) =>
        state.configurationState && state.configurationState.configuration.partnerConfig.isPasswordsAvailable
            ? state.configurationState.configuration.partnerConfig.isPasswordsAvailable
            : false
    );
    const routeLoading = useSelector((state: ApplicationState) => state.configurationState && (state.configurationState.loading || false));
    const { t: tLang } = useTranslation();

    const showAddPasswordDialog = () => {
        setAddPasswordDialogIsVisible(true);
        window.addToDataLayer({ event: "Add New Password" });
    };

    const closeAddPasswordDialog = (): any => {
        setAddPasswordDialogIsVisible(false);
    };

    const showViewPasswordDialog = () => {
        setViewPasswordDialogIsVisible(true);
        window.addToDataLayer({ event: "View Password" });
    };

    const closeViewPasswordDialog = (): any => {
        setViewPasswordDialogIsVisible(false);
    };

    const showEditPasswordDialog = () => {
        setEditPasswordDialogIsVisible(true);
    };

    const closeEditPasswordDialog = (): any => {
        setEditPasswordDialogIsVisible(false);
    };

    const showDeletePasswordDialog = () => {
        setDeletePasswordDialogIsVisible(true);
    };

    const closeDeletePasswordDialog = (): any => {
        setDeletePasswordDialogIsVisible(false);
    };

    const searchHandleChange = (event: any) => {
        event.preventDefault();

        setState({
            ...state,
            search: event.target.value,
        });
    };

    useEffect(() => {
        const initialUrl = window.location.origin + "/app/identity-vault";
        sessionStorage.setItem("initialUrl", encodeURIComponent(initialUrl));
        if (!isPasswordsAvailable) {
            history.push("/identity-vault/secure-storage");
        } else {
            requestRequestPasswordList();
        }
    }, []);

    useEffect(() => {
        if (routeLoading) {
            dispatch(ConfigurationActions.actionCreators.setRouteLoading(false));
        }
    }, [dispatch, routeLoading]);

    return (
        <React.Fragment>
            <div className=" row">
                <div className="col-xs-12 row">
                    <h1 className="title-page" id="password-manager-page" tabIndex={-1}>
                        {tLang("identityVault.title")}
                    </h1>
                    <LogoContainer internalPage={true} />
                </div>
            </div>

            <IdentityVaultTab selectedTab="3" />

            <div className="row" id="content">
                <div className="col-xs">
                    <div className="box offset-padding">
                        <PasswordManagerHeader />

                        <div className="box-content">
                            <div className="row between-xs">
                                <div className="col-xs col-gutter-vertical flex-grow-null">
                                    <div className="form-row inline no-margin">
                                        <div className="form-label prefix-label">
                                            <label htmlFor="search-passwords">
                                                <span className="sr-only">{tLang("identityVault.passwordManager.placeholder.searchPassword")}</span>
                                                <em className="las la-search"></em>
                                            </label>
                                        </div>
                                        <div className="form-input ">
                                            <div role="alert" className="form-input-content">
                                                <input
                                                    aria-required="true"
                                                    type="text"
                                                    className="inputbox"
                                                    value={state.search}
                                                    name="search"
                                                    id="search-passwords"
                                                    required
                                                    placeholder={tLang("identityVault.passwordManager.placeholder.searchPassword") + "..."}
                                                    onChange={searchHandleChange}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-xs col-gutter-vertical flex-grow-null">
                                    <button
                                        type="button"
                                        className="btn-default btn-primary"
                                        id="password-manager"
                                        data-a11y-dialog-show="dialog-add-password"
                                        onClick={() => showAddPasswordDialog()}
                                    >
                                        <span className="btn-text">{tLang("identityVault.passwordManager.addPassword")}</span>
                                    </button>
                                </div>
                            </div>

                            <div role="status" id="filterResults" className="sr-only" aria-live="polite"></div>

                            {passwordList.length <= 0 || passwordList[0].hasError ? (
                                <div className={passwordListLoading ? "msg-generic msg-small margin-top-xs loading" : "msg-generic msg-small margin-top-xs"}>
                                    <RenderIcons iconName="info-circle" /> {tLang("identityVault.passwordManager.noPassword")}
                                </div>
                            ) : (
                                <div role="list" className="data-grid has-lines margin-top-xs" id="passwords-grid">
                                    <PasswordList favourite={true} showDialog={showViewPasswordDialog} search={state.search.toLowerCase()} />
                                    <PasswordList favourite={false} showDialog={showViewPasswordDialog} search={state.search.toLowerCase()} />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {addPasswordDialogIsVisible && (
                <DialogComponent dialogId="dialog-add-password" title="Add Password" closeDialog={closeAddPasswordDialog}>
                    <AddPassword closeDialog={closeAddPasswordDialog} />
                </DialogComponent>
            )}

            {viewPasswordDialogIsVisible && (
                <DialogComponent dialogId="dialog-view-password" title="View Password" closeDialog={closeViewPasswordDialog}>
                    <ViewPassword closeDialog={closeViewPasswordDialog} showEditPasswordDialog={showEditPasswordDialog} />
                </DialogComponent>
            )}

            {editPasswordDialogIsVisible && (
                <DialogComponent dialogId="dialog-edit-password" title="View Password" closeDialog={closeEditPasswordDialog}>
                    <EditPassword closeDialog={closeEditPasswordDialog} showDeletePasswordDialog={showDeletePasswordDialog} />
                </DialogComponent>
            )}
            {deletePasswordDialogIsVisible && (
                <DialogComponent dialogId="dialog-delete-password" title="View Password" closeDialog={closeDeletePasswordDialog}>
                    <ConfirmDeletePassword closeDialog={closeDeletePasswordDialog} closeEditPasswordDialog={closeEditPasswordDialog} />
                </DialogComponent>
            )}
        </React.Fragment>
    );
};

export default PasswordManager;
